import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingBag, Monitor, ChefHat, LayoutDashboard, ArrowLeft, Lock, User, Eye, EyeOff, Loader2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';

const RoleSwitcher = () => {
  const navigate = useNavigate();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const roles = [
    {
      title: 'Customer',
      description: 'Browse menu and place orders',
      icon: ShoppingBag,
      path: '/',
      color: 'from-[#FF4500] to-[#FF5722]',
      requiresLogin: false
    },
    {
      title: 'POS System',
      description: 'Billing and order management',
      icon: Monitor,
      path: '/pos',
      color: 'from-[#00E5FF] to-[#00B8D4]',
      requiresLogin: true,
      allowedRoles: ['admin', 'pos', 'manager']
    },
    {
      title: 'Kitchen Display',
      description: 'View and manage orders',
      icon: ChefHat,
      path: '/kitchen',
      color: 'from-[#00C853] to-[#00E676]',
      requiresLogin: true,
      allowedRoles: ['admin', 'kitchen', 'manager']
    },
    {
      title: 'Admin Dashboard',
      description: 'Analytics and management',
      icon: LayoutDashboard,
      path: '/admin',
      color: 'from-[#FFD600] to-[#FFEA00]',
      requiresLogin: true,
      allowedRoles: ['admin']
    }
  ];

  const handleRoleClick = (role) => {
    if (!role.requiresLogin) {
      navigate(role.path);
    } else {
      // Check if already logged in
      const staffData = localStorage.getItem('staffData');
      if (staffData) {
        const staff = JSON.parse(staffData);
        if (role.allowedRoles.includes(staff.role)) {
          navigate(role.path);
          return;
        }
      }
      // Show login modal
      setSelectedRole(role);
      setShowLoginModal(true);
      setUsername('');
      setPassword('');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!username.trim() || !password.trim()) {
      toast.error('Please enter username and password');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/staff/login', {
        username: username.trim(),
        password: password.trim()
      });

      const { staff, token } = response.data;

      // Check if staff has permission for the selected role
      if (!selectedRole.allowedRoles.includes(staff.role)) {
        toast.error(`You don't have access to ${selectedRole.title}. Your role: ${staff.role}`);
        setLoading(false);
        return;
      }

      // Save staff data
      localStorage.setItem('staffData', JSON.stringify(staff));
      localStorage.setItem('staffToken', token);

      toast.success(`Welcome, ${staff.full_name}!`);
      setShowLoginModal(false);
      navigate(selectedRole.path);
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid username or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Back to Customer App Button */}
        <div className="mb-6">
          <Link to="/">
            <Button variant="ghost" className="text-gray-400 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Menu
            </Button>
          </Link>
        </div>

        <div className="text-center mb-8 sm:mb-12">
          {/* Logo */}
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/logo.png" 
              alt="Fish on Fire" 
              className="h-20 sm:h-28 md:h-32 w-auto object-contain"
            />
          </div>
          <p className="text-gray-400 text-base sm:text-lg">Select a system to access</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {roles.map((role) => {
            const Icon = role.icon;
            return (
              <div 
                key={role.path} 
                onClick={() => handleRoleClick(role)}
                data-testid={`role-${role.title.toLowerCase().replace(' ', '-')}`}
              >
                <Card className="bg-[#121212] border-white/10 hover:border-[#FF4500]/50 transition-all hover:scale-105 cursor-pointer group h-full">
                  <CardHeader className="text-center p-6 sm:p-8">
                    <div className={`w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 rounded-xl bg-gradient-to-br ${role.color} flex items-center justify-center group-hover:scale-110 transition-transform relative`}>
                      <Icon className="w-8 h-8 sm:w-10 sm:h-10 text-white" strokeWidth={1.5} />
                      {role.requiresLogin && (
                        <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-[#121212] rounded-full flex items-center justify-center border-2 border-white/20">
                          <Lock className="w-3 h-3 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <CardTitle className="text-xl sm:text-2xl text-white mb-2" style={{ fontFamily: 'Bebas Neue' }}>
                      {role.title}
                    </CardTitle>
                    <CardDescription className="text-gray-400 text-sm">
                      {role.description}
                    </CardDescription>
                    {role.requiresLogin && (
                      <p className="text-xs text-gray-500 mt-2">Staff login required</p>
                    )}
                  </CardHeader>
                </Card>
              </div>
            );
          })}
        </div>
      </div>

      {/* Staff Login Modal */}
      <Dialog open={showLoginModal} onOpenChange={setShowLoginModal}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Bebas Neue' }}>
              <Lock className="w-6 h-6 text-[#FF4500]" />
              Staff Login
            </DialogTitle>
          </DialogHeader>
          
          {selectedRole && (
            <div className="mb-4 p-3 bg-white/5 rounded-lg border border-white/10">
              <p className="text-sm text-gray-400">Accessing:</p>
              <p className="text-lg font-bold text-white" style={{ fontFamily: 'Bebas Neue' }}>
                {selectedRole.title}
              </p>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-gray-300">Username / Staff ID</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your username"
                  className="bg-white/5 border-white/10 text-white pl-10"
                  autoFocus
                  data-testid="staff-username"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-300">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="bg-white/5 border-white/10 text-white pl-10 pr-10"
                  data-testid="staff-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FF4500] hover:bg-[#FF4500]/80 text-white"
              data-testid="staff-login-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Logging in...
                </>
              ) : (
                'Login'
              )}
            </Button>
          </form>

          <p className="text-xs text-gray-500 text-center mt-4">
            Contact your administrator if you don't have login credentials
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoleSwitcher;
